import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "../authorization/authService";

export const templateCommentApi = createApi({
  reducerPath: "templateCommentApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["TemplateComment"],
  endpoints: (builder) => ({
    // Get comments for a specific template within an application
    getTemplateComments: builder.query({
      query: ({ application_id, self_declaration_template_id }) => ({
        url: `/template_comment/applications/${application_id}/templates/${self_declaration_template_id}/comments`,
        method: "GET",
      }),
      providesTags: (
        result,
        error,
        { application_id, self_declaration_template_id }
      ) => [
        {
          type: "TemplateComment",
          id: `${application_id}-${self_declaration_template_id}`,
        },
      ],
    }),

    // Get all template comments for an application (across all templates)
    getAllTemplateCommentsForApplication: builder.query({
      query: ({ application_id }) => ({
        url: `/template_comment/applications/${application_id}/template-comments`,
        method: "GET",
      }),
      providesTags: (result, error, { application_id }) => [
        { type: "TemplateComment", id: `app-${application_id}` },
      ],
    }),

    // Create a comment for a specific template within an application
    createTemplateComment: builder.mutation({
      query: ({
        application_id,
        self_declaration_template_id,
        commentData,
      }) => ({
        url: `/template_comment/applications/${application_id}/templates/${self_declaration_template_id}/comments`,
        method: "POST",
        body: commentData,
      }),
      invalidatesTags: (
        result,
        error,
        { application_id, self_declaration_template_id }
      ) => [
        {
          type: "TemplateComment",
          id: `${application_id}-${self_declaration_template_id}`,
        },
        { type: "TemplateComment", id: `app-${application_id}` },
      ],
    }),

    // Create a reply to an existing template comment
    createTemplateCommentReply: builder.mutation({
      query: ({ parent_comment_id, commentData }) => ({
        url: `/template_comment/template-comments/${parent_comment_id}/replies`,
        method: "POST",
        body: commentData,
      }),
      invalidatesTags: (
        result,
        error,
        { application_id, self_declaration_template_id }
      ) => [
        {
          type: "TemplateComment",
          id: `${application_id}-${self_declaration_template_id}`,
        },
        { type: "TemplateComment", id: `app-${application_id}` },
      ],
    }),

    // Update a template comment
    updateTemplateComment: builder.mutation({
      query: ({ comment_id, commentData }) => ({
        url: `/template_comment/template-comments/${comment_id}`,
        method: "PUT",
        body: commentData,
      }),
      invalidatesTags: (
        result,
        error,
        { application_id, self_declaration_template_id }
      ) => [
        {
          type: "TemplateComment",
          id: `${application_id}-${self_declaration_template_id}`,
        },
        { type: "TemplateComment", id: `app-${application_id}` },
      ],
    }),

    // Delete a template comment
    deleteTemplateComment: builder.mutation({
      query: ({ comment_id }) => ({
        url: `/template_comment/template-comments/${comment_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (
        result,
        error,
        { application_id, self_declaration_template_id }
      ) => [
        {
          type: "TemplateComment",
          id: `${application_id}-${self_declaration_template_id}`,
        },
        { type: "TemplateComment", id: `app-${application_id}` },
      ],
    }),

    // Get a specific comment with its replies
    getTemplateCommentWithReplies: builder.query({
      query: ({ comment_id }) => ({
        url: `/template_comment/template-comments/${comment_id}`,
        method: "GET",
      }),
      providesTags: (result, error, { comment_id }) => [
        { type: "TemplateComment", id: `comment-${comment_id}` },
      ],
    }),
  }),
});

export const {
  useGetTemplateCommentsQuery,
  useGetAllTemplateCommentsForApplicationQuery,
  useCreateTemplateCommentMutation,
  useCreateTemplateCommentReplyMutation,
  useUpdateTemplateCommentMutation,
  useDeleteTemplateCommentMutation,
  useGetTemplateCommentWithRepliesQuery,
} = templateCommentApi;
